import React, { useState } from 'react';
import { useCookieConsent, COOKIE_CATEGORIES } from '../context/CookieContext';
import { useLanguage } from '../context/LanguageContext';
import { Cookie, Settings, X, Check, ChevronDown, ChevronUp, Shield, BarChart3, Target } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const CookieBanner = () => {
  const { language } = useLanguage();
  const { 
    showBanner, 
    showSettings, 
    consent,
    acceptAll, 
    rejectNonEssential, 
    savePreferences,
    openSettings,
    closeSettings 
  } = useCookieConsent();

  const [preferences, setPreferences] = useState({
    functional: consent.functional,
    analytics: consent.analytics,
    marketing: consent.marketing
  });

  const [expandedSection, setExpandedSection] = useState(null);

  // Translations
  const t = {
    sv: {
      title: 'Vi använder cookies',
      description: 'Vi använder cookies för att förbättra din upplevelse på vår webbplats, analysera trafik och visa relevanta annonser. Genom att klicka på "Acceptera alla" godkänner du användningen av alla cookies.',
      acceptAll: 'Acceptera alla',
      rejectAll: 'Endast nödvändiga',
      customize: 'Anpassa',
      savePreferences: 'Spara inställningar',
      learnMore: 'Läs mer om vår cookie-policy',
      settings: 'Cookie-inställningar',
      necessary: {
        title: 'Nödvändiga cookies',
        description: 'Dessa cookies är nödvändiga för att webbplatsen ska fungera korrekt och kan inte stängas av.',
        details: 'Inkluderar: Språkval, sessionshantering, säkerhetsfunktioner, cookie-samtycke.'
      },
      functional: {
        title: 'Funktionella cookies',
        description: 'Förbättrar funktionalitet och personalisering av webbplatsen.',
        details: 'Inkluderar: Sparade användarpreferenser, formulärdata, priskalkylatorbeteende.'
      },
      analytics: {
        title: 'Analytiska cookies',
        description: 'Hjälper oss att förstå hur besökare använder webbplatsen för att förbättra prestanda.',
        details: 'Inkluderar: Google Analytics (med IP-anonymisering), besöksstatistik, sidprestanda.'
      },
      marketing: {
        title: 'Marknadsföringscookies',
        description: 'Används för att visa relevanta annonser på sociala medier och andra plattformar.',
        details: 'Inkluderar: Facebook Pixel, Instagram-integrationer, Google Ads.'
      },
      alwaysActive: 'Alltid aktiv'
    },
    en: {
      title: 'We use cookies',
      description: 'We use cookies to improve your experience on our website, analyze traffic, and show relevant ads. By clicking "Accept all" you consent to the use of all cookies.',
      acceptAll: 'Accept all',
      rejectAll: 'Only necessary',
      customize: 'Customize',
      savePreferences: 'Save preferences',
      learnMore: 'Learn more about our cookie policy',
      settings: 'Cookie Settings',
      necessary: {
        title: 'Necessary cookies',
        description: 'These cookies are essential for the website to function properly and cannot be disabled.',
        details: 'Includes: Language selection, session management, security features, cookie consent.'
      },
      functional: {
        title: 'Functional cookies',
        description: 'Enhance functionality and personalization of the website.',
        details: 'Includes: Saved user preferences, form data, price calculator behavior.'
      },
      analytics: {
        title: 'Analytical cookies',
        description: 'Help us understand how visitors use the website to improve performance.',
        details: 'Includes: Google Analytics (with IP anonymization), visitor statistics, page performance.'
      },
      marketing: {
        title: 'Marketing cookies',
        description: 'Used to show relevant ads on social media and other platforms.',
        details: 'Includes: Facebook Pixel, Instagram integrations, Google Ads.'
      },
      alwaysActive: 'Always active'
    }
  };

  const text = t[language] || t.sv;

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const categoryIcons = {
    necessary: Shield,
    functional: Settings,
    analytics: BarChart3,
    marketing: Target
  };

  // Don't render if banner shouldn't show
  if (!showBanner && !showSettings) return null;

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && !showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 animate-slide-up">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Cookie className="text-purple-600" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {text.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {text.description}
                  </p>
                  <a 
                    href="/cookies" 
                    className="text-sm text-purple-600 hover:text-purple-700 underline"
                  >
                    {text.learnMore}
                  </a>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  onClick={acceptAll}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Check size={18} className="mr-2" />
                  {text.acceptAll}
                </Button>
                <Button
                  variant="outline"
                  onClick={rejectNonEssential}
                  className="flex-1"
                >
                  {text.rejectAll}
                </Button>
                <Button
                  variant="outline"
                  onClick={openSettings}
                  className="flex-1"
                >
                  <Settings size={18} className="mr-2" />
                  {text.customize}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={(open) => !open && closeSettings()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="text-purple-600" size={24} />
              {text.settings}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Necessary Cookies - Always on */}
            <CookieCategory
              icon={categoryIcons.necessary}
              title={text.necessary.title}
              description={text.necessary.description}
              details={text.necessary.details}
              isExpanded={expandedSection === 'necessary'}
              onToggleExpand={() => toggleSection('necessary')}
              checked={true}
              disabled={true}
              alwaysActiveText={text.alwaysActive}
            />

            {/* Functional Cookies */}
            <CookieCategory
              icon={categoryIcons.functional}
              title={text.functional.title}
              description={text.functional.description}
              details={text.functional.details}
              isExpanded={expandedSection === 'functional'}
              onToggleExpand={() => toggleSection('functional')}
              checked={preferences.functional}
              onChange={(checked) => setPreferences(prev => ({ ...prev, functional: checked }))}
            />

            {/* Analytics Cookies */}
            <CookieCategory
              icon={categoryIcons.analytics}
              title={text.analytics.title}
              description={text.analytics.description}
              details={text.analytics.details}
              isExpanded={expandedSection === 'analytics'}
              onToggleExpand={() => toggleSection('analytics')}
              checked={preferences.analytics}
              onChange={(checked) => setPreferences(prev => ({ ...prev, analytics: checked }))}
            />

            {/* Marketing Cookies */}
            <CookieCategory
              icon={categoryIcons.marketing}
              title={text.marketing.title}
              description={text.marketing.description}
              details={text.marketing.details}
              isExpanded={expandedSection === 'marketing'}
              onToggleExpand={() => toggleSection('marketing')}
              checked={preferences.marketing}
              onChange={(checked) => setPreferences(prev => ({ ...prev, marketing: checked }))}
            />
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={rejectNonEssential}
              className="flex-1"
            >
              {text.rejectAll}
            </Button>
            <Button
              onClick={handleSavePreferences}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
            >
              {text.savePreferences}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Cookie Category Component
const CookieCategory = ({ 
  icon: Icon, 
  title, 
  description, 
  details,
  isExpanded,
  onToggleExpand,
  checked, 
  onChange, 
  disabled,
  alwaysActiveText 
}) => {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="p-4 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
              <Icon className="text-purple-600" size={20} />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">{title}</h4>
              <p className="text-sm text-gray-500">{description}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {disabled ? (
              <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                {alwaysActiveText}
              </span>
            ) : (
              <Switch
                checked={checked}
                onCheckedChange={onChange}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Expandable details */}
      <button
        onClick={onToggleExpand}
        className="w-full px-4 py-2 flex items-center justify-between text-sm text-gray-500 hover:bg-gray-50 transition-colors"
      >
        <span>Visa detaljer</span>
        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      
      {isExpanded && (
        <div className="px-4 pb-4 text-sm text-gray-600 bg-white">
          {details}
        </div>
      )}
    </div>
  );
};

export default CookieBanner;
